/**
 * Spec 070 — number, month and delta formatting for the business status strip.
 * #1331 — the windows follow the selected {@link BusinessStatusPeriod} (30 days by default).
 * Numbers use en grouping ("1,240"); months are `YYYY-MM` Dubai calendar months from the API.
 */

import { BusinessStatusPeriod } from '../../types/audit';

const NF = new Intl.NumberFormat('en-US');

export interface PeriodText {
  /** Selector label: "Week". */
  option: string;
  /** "last 7 days". */
  last: string;
  /** "previous 7 days". */
  previous: string;
  /** "7 days" (the header's "deltas vs the 7 days before"). */
  span: string;
  /** "7-day" ("none in either 7-day period"). */
  adjective: string;
}

export const BUSINESS_STATUS_PERIODS: BusinessStatusPeriod[] = ['DAY', 'WEEK', 'MONTH'];
export const DEFAULT_BUSINESS_STATUS_PERIOD: BusinessStatusPeriod = 'MONTH';

export const PERIOD_TEXT: Record<BusinessStatusPeriod, PeriodText> = {
  DAY: { option: 'Day', last: 'last 24 hours', previous: 'previous 24 hours', span: '24 hours', adjective: '24-hour' },
  WEEK: { option: 'Week', last: 'last 7 days', previous: 'previous 7 days', span: '7 days', adjective: '7-day' },
  MONTH: { option: 'Month', last: 'last 30 days', previous: 'previous 30 days', span: '30 days', adjective: '30-day' },
};

/** The remembered choice (#1331, per browser); anything unreadable or unknown is the default. */
export const PERIOD_STORAGE_KEY = 'garagat.admin.business-status-period';

export function readStoredPeriod(): BusinessStatusPeriod {
  try {
    const v = localStorage.getItem(PERIOD_STORAGE_KEY);
    return (BUSINESS_STATUS_PERIODS as string[]).includes(v ?? '') ? (v as BusinessStatusPeriod) : DEFAULT_BUSINESS_STATUS_PERIOD;
  } catch {
    return DEFAULT_BUSINESS_STATUS_PERIOD;
  }
}

export function storePeriod(period: BusinessStatusPeriod): void {
  try {
    localStorage.setItem(PERIOD_STORAGE_KEY, period);
  } catch {
    // Private mode or blocked storage: the choice just isn't remembered.
  }
}

/** "1,240". */
export function formatCount(n: number): string {
  return NF.format(Number.isFinite(n) ? n : 0);
}

export type DeltaKind = 'up' | 'down' | 'flat';

export interface Delta {
  kind: DeltaKind;
  /** Visible badge text (the arrow glyph is drawn separately, aria-hidden). */
  text: string;
  /** Screen-reader wording of the badge; colour and glyph are never the only signal. */
  sr: string;
  /** "vs previous 30 days (112)" — only when there is a baseline to compare with. */
  comparison: string | null;
}

/**
 * AC-8 delta rules:
 * - previous > 0 → ▲/▼ x% (rounded integer) plus "vs previous 30 days (previous)"; 0% reads neutral;
 * - previous = 0 and new > 0 → "+N new", no % (a % of nothing would overstate it);
 * - both 0 → neutral "no change".
 */
export function businessDelta(recent: number, previous: number,
  period: BusinessStatusPeriod = DEFAULT_BUSINESS_STATUS_PERIOD): Delta {
  const text = PERIOD_TEXT[period];
  const r = Math.max(0, recent || 0);
  const p = Math.max(0, previous || 0);
  if (p > 0) {
    const pct = Math.round(((r - p) / p) * 100);
    const comparison = `vs ${text.previous} (${formatCount(p)})`;
    if (pct > 0) return { kind: 'up', text: `${pct}%`, sr: `Up ${pct}%`, comparison };
    if (pct < 0) return { kind: 'down', text: `${Math.abs(pct)}%`, sr: `Down ${Math.abs(pct)}%`, comparison };
    return { kind: 'flat', text: '0%', sr: 'No change', comparison };
  }
  if (r > 0) {
    return { kind: 'up', text: `+${formatCount(r)} new`, sr: `+${formatCount(r)} new, none in the ${text.previous}`, comparison: null };
  }
  return { kind: 'flat', text: 'no change', sr: `No change, none in either ${text.adjective} period`, comparison: null };
}

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function parseMonth(month: string): { y: number; m: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!match) return null;
  const m = Number(match[2]) - 1;
  if (m < 0 || m > 11) return null;
  return { y: Number(match[1]), m };
}

/** "2026-05" → "May". Unparseable input is returned as is. */
export function monthShort(month: string): string {
  const p = parseMonth(month);
  return p ? SHORT[p.m] : month;
}

/** "2026-05" → "May 2026". */
export function monthLong(month: string): string {
  const p = parseMonth(month);
  return p ? `${LONG[p.m]} ${p.y}` : month;
}

/** "2026-05" → "May"; the year is added only when needed by the caller. */
export function monthName(month: string): string {
  const p = parseMonth(month);
  return p ? LONG[p.m] : month;
}

/** "2026-05" → 2026 (NaN when unparseable). */
export function monthYear(month: string): number {
  const p = parseMonth(month);
  return p ? p.y : NaN;
}
